<template>
  <div class="mods-panel">
    <div class="mods-panel__accent" />

    <div class="mods-panel__left">
      <div class="mods-panel__stat-row">
        <div class="mods-panel__stat-head">
          <div class="mods-panel__stat">
            <div class="mods-panel__stat-icon mods-panel__stat-icon--select">
              <v-icon small color="white">mdi-checkbox-marked-outline</v-icon>
            </div>
            <div>
              <div class="mods-panel__stat-label">Выбрано</div>
              <div class="mods-panel__stat-value">
                {{ selected.length }}
                <span v-if="totalDoorphones" class="mods-panel__stat-max">/ {{ totalDoorphones }}</span>
                <span v-else class="mods-panel__stat-max">шт.</span>
              </div>
            </div>
          </div>
          <v-btn
            small
            rounded
            class="text-none mods-panel__select-table"
            color="primary"
            outlined
            :loading="selectingAllFromTable"
            :disabled="!totalDoorphones"
            @click="$emit('select-from-table')"
          >
            <v-icon left small>mdi-table-large</v-icon>
            Все из таблицы
            <span v-if="totalDoorphones" class="ml-1">({{ totalDoorphones }})</span>
          </v-btn>
        </div>
        <v-progress-linear
          :value="selectionPercent"
          height="6"
          rounded
          color="primary"
          background-color="blue lighten-5"
          class="mods-panel__progress"
        />
      </div>

      <div v-if="modelGroups.length" class="mods-panel__models">
        <v-chip
          v-for="group in modelGroups"
          :key="group.key"
          small
          label
          class="mr-1 mb-1"
          :color="soundReady[group.key] ? 'green lighten-4' : 'indigo lighten-5'"
          :text-color="soundReady[group.key] ? 'green darken-3' : 'indigo darken-2'"
        >
          <v-icon left x-small>
            {{ soundReady[group.key] ? 'mdi-check-circle' : 'mdi-memory' }}
          </v-icon>
          {{ group.label }} · {{ group.count }}
        </v-chip>
      </div>
      <div v-else class="mods-panel__empty-hint">
        <v-icon x-small class="mr-1" color="grey">mdi-information-outline</v-icon>
        Отметьте домофоны в списке или на карте
      </div>

      <div v-if="unsupportedCount" class="mods-panel__warn mb-2">
        <v-icon x-small class="mr-1" color="orange darken-2">mdi-alert-outline</v-icon>
        {{ unsupportedCount }} без поддержки аудио (не Beward / Rubetek)
      </div>
      <div v-if="selectedAll" class="mods-panel__warn mb-2" style="color: #1565c0">
        <v-icon x-small class="mr-1" color="primary">mdi-check-all</v-icon>
        Выбраны все домофоны из таблицы
      </div>

      <div class="mods-panel__actions">
        <v-btn
          small
          rounded
          class="text-none"
          color="primary"
          outlined
          :loading="selectingAll"
          :disabled="!totalDoorphones"
          @click="$emit('select-by-filter')"
        >
          <v-icon left small>mdi-select-all</v-icon>
          Все по фильтру
        </v-btn>
        <v-btn
          small
          rounded
          class="text-none"
          outlined
          :disabled="!selected.length"
          @click="$emit('clear')"
        >
          <v-icon left small>mdi-close-box-outline</v-icon>
          Очистить
        </v-btn>
      </div>

      <mass-schedule-fields v-model="scheduleLocal" class="mt-2" />
    </div>

    <div class="mods-panel__divider" />

    <div class="mods-panel__right">
      <div class="mods-panel__upload-title">
        <v-icon small color="primary" class="mr-1">mdi-music-note</v-icon>
        Аудиофайлы по типам устройств
      </div>

      <v-alert
        v-if="modelGroups.length > 1"
        dense
        colored-border
        border="left"
        color="primary"
        class="mb-3"
      >
        В выборе несколько типов устройств — загрузите отдельный .wav для каждого.
      </v-alert>

      <div v-if="!modelGroups.length" class="mods-panel__empty-hint mb-3">
        Нет устройств с поддержкой загрузки аудио
      </div>

      <div
        v-for="group in modelGroups"
        :key="'sound-' + group.key"
        class="mods-panel__sound-row"
      >
        <div class="mods-panel__sound-meta">
          <span class="mods-panel__sound-label">{{ group.label }}</span>
          <span class="mods-panel__sound-count">{{ group.count }} шт.</span>
        </div>
        <v-file-input
          :value="localSounds[group.key] || null"
          accept=".wav"
          outlined
          dense
          hide-details
          :label="`Файл для ${group.label}`"
          prepend-icon=""
          prepend-inner-icon="mdi-file-music-outline"
          clearable
          class="border-rounding"
          @change="onSoundChange(group.key, $event)"
        />
      </div>

      <div class="mods-panel__cta">
        <v-btn
          color="success"
          class="text-none"
          rounded
          elevation="0"
          :disabled="!canUpload"
          :loading="uploading"
          @click="$emit('upload')"
        >
          <v-icon left small>mdi-cloud-upload-outline</v-icon>
          Запустить процесс
        </v-btn>
        <v-btn
          color="primary"
          class="text-none"
          rounded
          outlined
          :disabled="!selected.length"
          :loading="resetting"
          @click="$emit('reset')"
        >
          <v-icon left small>mdi-restore</v-icon>
          Сброс на дефолтные аудио
        </v-btn>
      </div>
      <div v-if="jobCount > 1" class="mods-panel__jobs-hint">
        <v-icon x-small class="mr-1">mdi-layers-outline</v-icon>
        Будет создано {{ jobCount }} задач по {{ maxCount }} домофонов
      </div>

      <v-alert
        v-if="hasActiveJob"
        dense
        colored-border
        border="left"
        type="info"
        class="mt-3 mb-0"
      >
        <div class="d-flex align-center mb-2">
          <v-icon small class="mr-2" color="primary">mdi-playlist-plus</v-icon>
          Уже есть активные загрузки. Новая партия попадёт в очередь и начнётся после текущих.
        </div>
        <div
          v-for="job in activeJobs"
          :key="'panel-job-' + job.mass_upload_id"
          class="mods-panel__queue-item"
        >
          <div class="mods-panel__queue-meta">
            <strong>{{ job.mass_upload_id }}</strong>
            <span class="mods-panel__queue-file">{{ job.file_name || 'аудио' }} · {{ job.total }} шт.</span>
            <span class="mods-panel__queue-status">
              {{ job.status === 'running' ? 'выполняется' : 'в очереди' }}
            </span>
          </div>
          <v-btn
            v-if="job.can_cancel || job.status === 'queued'"
            x-small
            text
            color="error"
            class="text-none"
            :loading="!!cancellingJobs[job.mass_upload_id]"
            @click="$emit('cancel-job', job)"
          >
            Отменить
          </v-btn>
        </div>
      </v-alert>

      <div class="mods-panel__hint">
        <v-icon x-small class="mr-1">mdi-waveform</v-icon>
        wav · до 200 КБ · 16000 Гц · 16 бит · отдельно для Beward / Rubetek RV-3434 / Rubetek RV-3438
      </div>
    </div>
  </div>
</template>

<script>
import MassScheduleFields from '@/components/massFunction/components/MassScheduleFields.vue';
import {
  getOpenDoorSoundModelKey,
  groupSelectedBySoundModel,
} from '@/components/massFunction/utils/openDoorSoundModelKey';

export default {
  name: 'MassOpenDoorSoundUploadPanel',
  components: { MassScheduleFields },
  props: {
    selected: {
      type: Array,
      default: () => [],
    },
    sounds: {
      type: Object,
      default: () => ({}),
    },
    schedule: {
      type: Object,
      required: true,
    },
    uploading: Boolean,
    resetting: Boolean,
    selectingAll: Boolean,
    selectingAllFromTable: Boolean,
    hasActiveJob: Boolean,
    activeJobs: {
      type: Array,
      default: () => [],
    },
    cancellingJobs: {
      type: Object,
      default: () => ({}),
    },
    maxCount: {
      type: Number,
      default: 1000,
    },
    totalDoorphones: {
      type: Number,
      default: 0,
    },
    jobCount: {
      type: Number,
      default: 0,
    },
    selectedAll: Boolean,
  },
  data () {
    return {
      localSounds: {},
      soundReady: {},
    };
  },
  computed: {
    scheduleLocal: {
      get () {
        return this.schedule;
      },
      set (val) {
        this.$emit('update:schedule', val);
      },
    },
    modelGroups () {
      return groupSelectedBySoundModel(this.selected);
    },
    unsupportedCount () {
      return this.selected.filter((item) => !getOpenDoorSoundModelKey(item.model)).length;
    },
    selectionPercent () {
      const denom = this.totalDoorphones || this.selected.length || 1;
      return Math.min(100, Math.round((this.selected.length / denom) * 100));
    },
    allSoundsReady () {
      if (!this.modelGroups.length) return false;
      return this.modelGroups.every((group) => !!this.soundReady[group.key]);
    },
    canUpload () {
      return this.selected.length > 0 && this.modelGroups.length > 0 && this.allSoundsReady && this.isScheduleReady;
    },
    isScheduleReady () {
      if (!this.schedule || !this.schedule.useSchedule) return true;
      return !!(this.schedule.startDate && this.schedule.endDate);
    },
  },
  watch: {
    sounds: {
      immediate: true,
      deep: true,
      handler (value) {
        const next = value || {};
        const nextKeys = Object.keys(next);

        Object.keys(this.localSounds).forEach((key) => {
          if (!nextKeys.includes(key) || !next[key]) {
            this.$set(this.localSounds, key, null);
            this.$set(this.soundReady, key, false);
          }
        });

        nextKeys.forEach((key) => {
          const file = this.normalizeSound(next[key]);
          this.$set(this.localSounds, key, file);
          this.$set(this.soundReady, key, !!file);
        });
      },
    },
    modelGroups () {
      this.emitSounds();
    },
  },
  methods: {
    isSoundFile (value) {
      if (!value) return false;
      if (Array.isArray(value)) {
        return value.some((item) => this.isSoundFile(item));
      }
      return (typeof File !== 'undefined' && value instanceof File) || (typeof Blob !== 'undefined' && value instanceof Blob) || (typeof value === 'object' && typeof value.name === 'string' && Number(value.size) > 0);
    },
    normalizeSound (value) {
      if (!value) return null;
      if (Array.isArray(value)) {
        const found = value.find((item) => this.isSoundFile(item));
        return found || null;
      }
      return this.isSoundFile(value) ? value : null;
    },
    emitSounds () {
      const payload = {};
      this.modelGroups.forEach((group) => {
        payload[group.key] = this.localSounds[group.key] || null;
      });
      this.$emit('update:sounds', payload);
    },
    onSoundChange (key, value) {
      const file = this.normalizeSound(value);
      this.$set(this.localSounds, key, file);
      this.$set(this.soundReady, key, !!file);
      this.emitSounds();
    },
  },
};
</script>

<style scoped>
.mods-panel {
  position: relative;
  display: flex;
  gap: 20px;
  padding: 18px 20px 16px;
  background: #fff;
  border: 1px solid #e3eaf3;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -6px 24px rgba(13, 64, 146, 0.1);
  flex-wrap: wrap;
  overflow: hidden;
}
.mods-panel__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #0d4092 0%, #1e88e5 55%, #26a69a 100%);
}
.mods-panel__left {
  flex: 1 1 360px;
}
.mods-panel__right {
  flex: 1 1 320px;
  min-width: 280px;
}
.mods-panel__divider {
  width: 1px;
  background: #e8eef5;
  align-self: stretch;
}
@media (max-width: 960px) {
  .mods-panel__divider {
    display: none;
  }
}
.mods-panel__stat-row {
  margin-bottom: 10px;
}
.mods-panel__stat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.mods-panel__stat {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.mods-panel__select-table {
  flex-shrink: 0;
  font-weight: 500;
}
.mods-panel__stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mods-panel__stat-icon--select {
  background: linear-gradient(135deg, #0d4092, #42a5f5);
}
.mods-panel__stat-label {
  font-size: 0.72rem;
  color: #78909c;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.mods-panel__stat-value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #0d4092;
  line-height: 1.1;
}
.mods-panel__stat-max {
  font-size: 0.9rem;
  font-weight: 500;
  color: #90a4ae;
}
.mods-panel__progress {
  max-width: 220px;
}
.mods-panel__models {
  margin-bottom: 10px;
}
.mods-panel__empty-hint {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: #90a4ae;
  margin-bottom: 10px;
}
.mods-panel__warn {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  color: #ef6c00;
}
.mods-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.mods-panel__upload-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.9rem;
  color: #37474f;
  margin-bottom: 8px;
}
.mods-panel__sound-row {
  margin-bottom: 12px;
}
.mods-panel__sound-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 4px;
}
.mods-panel__sound-label {
  font-size: 0.84rem;
  font-weight: 650;
  color: #263238;
}
.mods-panel__sound-count {
  font-size: 0.72rem;
  color: #90a4ae;
}
.mods-panel__cta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.mods-panel__jobs-hint {
  display: flex;
  align-items: center;
  margin-top: 8px;
  font-size: 0.76rem;
  color: #1565c0;
}
.mods-panel__hint {
  display: flex;
  align-items: center;
  margin-top: 10px;
  font-size: 0.72rem;
  color: #90a4ae;
}
.mods-panel__queue-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0 0;
  border-top: 1px solid rgba(13, 64, 146, 0.08);
  margin-top: 6px;
}
.mods-panel__queue-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  font-size: 0.78rem;
  color: #455a64;
}
.mods-panel__queue-file {
  color: #78909c;
}
.mods-panel__queue-status {
  text-transform: lowercase;
  color: #1565c0;
}
</style>
